import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ThemeToggle } from '@/lib/theme';
import { ConfirmDialog } from '@/components/ui';
import { Permission } from '@cr/shared';

/** Пункт показывается, только если у роли есть соответствующее право. */
const NAV: Array<{ to: string; label: string; permission?: string; ready?: boolean }> = [
  { to: '/', label: 'Обзор', ready: true },
  { to: '/categories', label: 'Категории', permission: Permission.CATEGORY_VIEW, ready: true },
  { to: '/products', label: 'Товары', permission: Permission.PRODUCT_VIEW, ready: true },
  { to: '/accessories', label: 'Аксессуары', permission: Permission.PRODUCT_VIEW, ready: true },
  { to: '/dictionaries', label: 'Справочники', permission: Permission.PRODUCT_VIEW, ready: true },
  {
    to: '/availability',
    label: 'Загрузка склада',
    permission: Permission.INVENTORY_VIEW,
    ready: true,
  },
  { to: '/promo', label: 'Промокоды', permission: Permission.PROMO_MANAGE, ready: true },
  { to: '/banners', label: 'Акции', permission: Permission.BANNER_MANAGE, ready: true },
  { to: '/orders', label: 'Заказы', permission: Permission.ORDER_VIEW, ready: true },
  { to: '/payments', label: 'Платежи', permission: Permission.PAYMENT_VIEW, ready: true },
  { to: '/returns', label: 'Возвраты', permission: Permission.RETURN_ACCEPT, ready: true },
  {
    to: '/notifications',
    label: 'Уведомления',
    permission: Permission.NOTIFICATION_MANAGE,
    ready: true,
  },
  { to: '/customers', label: 'Клиенты', permission: Permission.CUSTOMER_VIEW, ready: true },
  { to: '/staff', label: 'Сотрудники', permission: Permission.ADMIN_MANAGE, ready: true },
  { to: '/audit', label: 'Журнал действий', permission: Permission.AUDIT_VIEW, ready: true },
  { to: '/settings', label: 'Настройки', permission: Permission.SETTINGS_MANAGE, ready: true },
];

export default function Layout({ children }: { children: ReactNode }) {
  const { admin, logout, can } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);

  return (
    <div className="flex min-h-full">
      <aside className="hidden w-56 shrink-0 border-r border-neutral-200 p-4 sm:block dark:border-neutral-800">
        <div className="mb-6 px-2">
          <p className="text-sm font-semibold">Аренда одежды</p>
          <p className="text-xs text-neutral-500">админ-панель</p>
        </div>

        <nav className="space-y-0.5">
          {NAV.filter((item) => !item.permission || can(item.permission)).map((item) =>
            item.ready ? (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2 text-sm transition ${
                    isActive
                      ? 'bg-neutral-100 font-medium dark:bg-neutral-800'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ) : (
              <div
                key={item.to}
                className="rounded-lg px-3 py-2 text-sm text-neutral-400 dark:text-neutral-600"
                title="Появится в следующих фазах"
              >
                {item.label}
              </div>
            ),
          )}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-neutral-200 px-6 py-3 dark:border-neutral-800">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{admin?.fullName}</p>
            <p className="truncate text-xs text-neutral-500">
              {admin?.email} · {admin?.role.code}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => setConfirmLogout(true)}
              className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm transition hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
            >
              Выйти
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        title="Выйти из панели?"
        description="Сессия закроется, для возврата понадобится войти заново."
        confirmLabel="Выйти"
        tone="danger"
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => {
          setConfirmLogout(false);
          void logout();
        }}
      />
    </div>
  );
}
